import React, { useState, useCallback, useContext, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AuthContext, DataStateContext, DataActionsContext, ClientsContext } from "@/App";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, X,
  ShieldAlert, Info, RefreshCw, Plus, Loader2, ArrowLeft, Copy,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────────
 * CUSTOMER CSV COLUMN MAP (from Rockpecker customer template)
 * Columns in the customer's file → RockLink fields
 * ────────────────────────────────────────────────────────────────────────── */
const CUSTOMER_COLS = [
  "PO Date", "Customer", "PO No.", "Item No.", "Part No.",
  "Item Description", "Due date", "(Blank)", "Requested Quantity",
  "Confirmed Quantity", "Requested Unit Price", "Dispatch due date",
  "Priority", "Comment", "Vendor", "QUAL",
];

const VALID_PRIORITIES = ["Standard", "Expedite"];
const DEFAULT_UNIT = "pcs";
const DEFAULT_STAGE = "PO Received";

const STAGE_BADGE_COLORS = {
  "PO Received":     "#f97316",
  "Procurement":     "#f59e0b",
  "Manufacturing":   "#8b5cf6",
  "Quality Control": "#14b8a6",
  "Dispatch":        "#f97316",
  "Completed":       "#22c55e",
};

/* ── CSV parser (handles quoted values, BOM, commas in quotes) ─────────────── */
function parseCSV(text) {
  const clean = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").trim();
  if (!clean) return { headers: [], rows: [] };
  const parseRow = (line) => {
    const out = [];
    let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i+1] === '"') { cur += '"'; i++; continue; }
      if (c === '"') { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out.map(v => v.trim());
  };
  const lines = clean.split("\n").filter(l => l.length > 0);
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(l => parseRow(l));
  return { headers, rows };
}

/* ── Date normaliser: accept DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, Excel serial ── */
function normaliseDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // ISO already
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // DD/MM/YYYY or DD-MM-YYYY
  const m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) {
    const d = m[1].padStart(2, "0");
    const mo = m[2].padStart(2, "0");
    let y = m[3];
    if (y.length === 2) y = (parseInt(y, 10) < 50 ? "20" : "19") + y;
    return `${y}-${mo}-${d}`;
  }
  // Excel serial
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (n > 20000 && n < 80000) {
      const dt = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
      return dt.toISOString().slice(0, 10);
    }
  }
  return s;
}

const toBool = (v) => {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["yes", "y", "true", "1", "qual", "x"].includes(s);
};

const toNum = (v) => {
  if (v == null || v === "") return 0;
  const n = parseFloat(String(v).replace(/[$,\s]/g, ""));
  return isNaN(n) ? 0 : n;
};

/* ──────────────────────────────────────────────────────────────────────────
 * TEMPLATE DOWNLOAD — matches the customer's format exactly (+ 2 sample rows)
 * ────────────────────────────────────────────────────────────────────────── */
function buildTemplateCSV() {
  const headers = CUSTOMER_COLS.join(",");
  const sample1 = [
    "01/03/2026", "BH Wireline", "PO-2026-100", "001", "RP-WPS-26-001",
    "\"Wireline Pressure Sub 4-1/2\"\"\"", "15/06/2026", "", "10", "10",
    "2500.00", "20/06/2026", "Standard", "Urgent casing order", "Ludhiana Precision", "Yes",
  ].join(",");
  const sample2 = [
    "03/03/2026", "SLB", "PO-2026-101", "001", "RP-CS-26-002",
    "Crossover Sub 3-1/2 IF", "20/06/2026", "", "20", "20",
    "925.00", "25/06/2026", "Expedite", "", "Patiala Forge", "No",
  ].join(",");
  return "\uFEFF" + [headers, sample1, sample2].join("\n") + "\n";
}

/* ──────────────────────────────────────────────────────────────────────────
 * MAIN COMPONENT
 * ────────────────────────────────────────────────────────────────────────── */
export default function BulkAddModal({ onClose }) {
  const { user } = useContext(AuthContext);
  const { items } = useContext(DataStateContext);
  const { bulkCreateItems, bulkUpdateItemsById, refreshData } = useContext(DataActionsContext);
  const { clients } = useContext(ClientsContext);

  const perms = user?.permissions || {};
  const isAdmin = user?.role === "Admin";
  const canImport = isAdmin || perms.can_import;

  const [step, setStep] = useState("upload");       // upload | review | done
  const [parsed, setParsed] = useState(null);        // {headers, rows, validRows, invalidRows, matches}
  const [decisions, setDecisions] = useState({});    // { rowIdx: "update"|"skip"|"duplicate" }
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);        // { created, updated, skipped }
  const fileInputRef = useRef(null);

  const validClientNames = useMemo(
    () => new Set((clients || []).map(c => (c.name || "").toLowerCase().trim()).filter(Boolean)),
    [clients]
  );
  const clientNameList = useMemo(
    () => (clients || []).map(c => c.name).filter(Boolean).sort(),
    [clients]
  );

  /* ── Download template ──────────────────────────────────────────────────── */
  const handleDownloadTemplate = useCallback(() => {
    const csv = buildTemplateCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rocklink_bulk_add_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }, []);

  /* ── File parsing / validation ─────────────────────────────────────────── */
  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { headers, rows } = parseCSV(ev.target.result);
        if (rows.length === 0) { toast.error("CSV has no data rows"); return; }

        const getIdx = (label) => headers.findIndex(h => h.toLowerCase().trim() === label.toLowerCase());
        const col = {
          po_date:          getIdx("PO Date"),
          client:           getIdx("Customer"),
          po_number:        getIdx("PO No."),
          line_number:      getIdx("Item No."),
          part_number:      getIdx("Part No."),
          item_description: getIdx("Item Description"),
          due_date:         getIdx("Due date"),
          quantity_req:     getIdx("Requested Quantity"),
          quantity_conf:    getIdx("Confirmed Quantity"),
          unit_price:       getIdx("Requested Unit Price"),
          dispatch_due:     getIdx("Dispatch due date"),
          priority:         getIdx("Priority"),
          comment:          getIdx("Comment"),
          vendor:           getIdx("Vendor"),
          qual:             getIdx("QUAL"),
        };

        // Required columns must be present in header
        const missing = ["po_number", "line_number", "item_description", "client", "due_date"]
          .filter(k => col[k] === -1)
          .map(k => ({ po_number: "PO No.", line_number: "Item No.", item_description: "Item Description", client: "Customer", due_date: "Due date" }[k]));
        if (missing.length) { toast.error(`Missing required columns: ${missing.join(", ")}`); return; }

        const validRows = [];
        const invalidRows = [];

        rows.forEach((r, idx) => {
          const g = (k) => col[k] >= 0 ? (r[col[k]] || "").trim() : "";
          const errors = [];
          const item_description = g("item_description");
          const po_number = g("po_number");
          const line_number = g("line_number") || "001";
          const part_number = g("part_number");
          const clientRaw = g("client");
          const qtyRaw = g("quantity_conf") || g("quantity_req");

          if (!item_description) errors.push("Missing Item Description");
          if (!po_number) errors.push("Missing PO No.");
          if (!clientRaw) errors.push("Missing Customer");
          else if (!validClientNames.has(clientRaw.toLowerCase())) errors.push(`Unknown Customer "${clientRaw}"`);
          const qty = toNum(qtyRaw);
          if (qty <= 0) errors.push("Quantity must be > 0");
          const due = normaliseDate(g("due_date"));
          if (!due) errors.push("Missing Due date");

          const priorityRaw = (g("priority") || "Standard").toLowerCase();
          let priority = "Standard";
          if (priorityRaw.startsWith("exp") || priorityRaw === "urgent" || priorityRaw === "rush") priority = "Expedite";
          else if (priorityRaw.startsWith("std") || priorityRaw === "standard" || priorityRaw === "normal" || !priorityRaw) priority = "Standard";

          const unit_price = toNum(g("unit_price"));

          const item = {
            item_description,
            po_number,
            part_number,
            line_number,
            revision: "",
            client: clientRaw,
            quantity: qty || 0,
            unit: DEFAULT_UNIT,
            vendor: g("vendor"),
            value_usd: Math.round(unit_price * (qty || 0) * 100) / 100,
            po_date: normaliseDate(g("po_date")),
            due_date: due,
            dispatch_due_date: normaliseDate(g("dispatch_due")),
            priority,
            stage: DEFAULT_STAGE,
            is_blocked: false,
            block_reason: "",
            stage_comment: g("comment"),
            is_qual: toBool(g("qual")),
            is_rework: false,
            return_reason: "",
          };

          if (errors.length) invalidRows.push({ idx, errors, item });
          else validRows.push({ idx, item });
        });

        // PO + Line match detection against existing items (exact uniqueness key)
        const poLineMap = new Map();
        const makeKey = (po, ln) => `${(po || "").trim().toLowerCase()}||${(ln || "").trim().toLowerCase()}`;
        (items || []).forEach(i => {
          const k = makeKey(i.po_number, i.line_number);
          if (i.po_number) {
            if (!poLineMap.has(k)) poLineMap.set(k, []);
            poLineMap.get(k).push(i);
          }
        });
        const matches = [];
        validRows.forEach(vr => {
          const k = makeKey(vr.item.po_number, vr.item.line_number);
          if (poLineMap.has(k)) {
            matches.push({ rowIdx: vr.idx, item: vr.item, existing: poLineMap.get(k) });
          }
        });
        const defaultDecisions = {};
        matches.forEach(m => { defaultDecisions[m.rowIdx] = "update"; });

        setParsed({ headers, rows, validRows, invalidRows, matches });
        setDecisions(defaultDecisions);
        setStep("review");
      } catch (e) {
        console.error(e);
        toast.error("Unable to read CSV. Check the file format.");
      }
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(file);
  }, [items, validClientNames]);

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = ""; // allow re-upload same file
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const applyToAll = (decision) => {
    if (!parsed) return;
    const next = {};
    parsed.matches.forEach(m => { next[m.rowIdx] = decision; });
    setDecisions(next);
  };

  const rowDecision = (idx) => decisions[idx] || "update";

  /* ── Final submit ───────────────────────────────────────────────────────── */
  const handleSubmit = useCallback(async () => {
    if (!parsed) return;
    setImporting(true);
    try {
      const matchByIdx = new Map(parsed.matches.map(m => [m.rowIdx, m]));
      const toCreate = [];
      const toUpdate = [];

      parsed.validRows.forEach(vr => {
        const match = matchByIdx.get(vr.idx);
        if (!match) {
          toCreate.push(vr.item);
          return;
        }
        const d = rowDecision(vr.idx);
        if (d === "skip") return;
        if (d === "duplicate") toCreate.push(vr.item);
        if (d === "update") {
          // Update first matching existing item.
          // A customer-PO revision must NEVER touch operational state, so strip
          // stage-owned fields the CSV doesn't carry (they're hardcoded to
          // defaults above — e.g. is_blocked:false — and would otherwise clear
          // a block/rework on every matched item). Same class of bug as the
          // shipped_at blanking issue: only send what the import actually owns.
          const target = match.existing[0];
          const {
            is_blocked, block_reason, is_rework, return_reason, stage,
            ...revisionFields
          } = vr.item;
          toUpdate.push({ id: target.id, fields: revisionFields });
        }
      });

      const CHUNK = 400;
      let created = 0; let updated = 0;
      if (toCreate.length) {
        for (let i = 0; i < toCreate.length; i += CHUNK) {
          setProgress(`Creating ${Math.min(i + CHUNK, toCreate.length)}/${toCreate.length}…`);
          const res = await bulkCreateItems(toCreate.slice(i, i + CHUNK), true);
          created += res?.count || 0;
        }
      }
      if (toUpdate.length) {
        for (let i = 0; i < toUpdate.length; i += CHUNK) {
          setProgress(`Updating ${Math.min(i + CHUNK, toUpdate.length)}/${toUpdate.length}…`);
          const res = await bulkUpdateItemsById(toUpdate.slice(i, i + CHUNK), true);
          updated += res?.updated || 0;
        }
      }

      setResult({ created, updated, skipped: parsed.invalidRows.length });
      setStep("done");
      await refreshData();
      toast.success(`Imported ${created} new · Updated ${updated}`);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setImporting(false);
      setProgress("");
    }
  }, [parsed, decisions, bulkCreateItems, bulkUpdateItemsById, refreshData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !importing) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[880px] lg:w-[960px] max-w-[95vw] max-h-[88vh] overflow-y-auto"
        data-testid="bulk-add-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5 text-orange-500" />
            Bulk Add / Update Items
          </DialogTitle>
          <DialogDescription>
            Upload the customer PO spreadsheet. Items with an existing PO No. + Item No. combination are flagged for review.
          </DialogDescription>
        </DialogHeader>

        {/* ─── Permission gate ─── */}
        {!canImport && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-500 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30">
            <ShieldAlert className="w-4 h-4" /> You do not have import permission.
          </div>
        )}

        {/* ─── STEP 1: UPLOAD ─── */}
        {canImport && step === "upload" && (
          <div className="space-y-4 pt-2">
            <div
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-[hsl(var(--border))] hover:border-orange-500/50 hover:bg-orange-500/5 rounded-lg p-8 text-center cursor-pointer transition-colors"
              data-testid="bulk-add-dropzone"
            >
              <Upload className="w-9 h-9 mx-auto mb-3 text-orange-500" />
              <p className="text-sm font-medium text-[hsl(var(--foreground))]">Drop CSV here or click to browse</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Customer PO template format — up to 2000 rows</p>
              <input
                ref={fileInputRef} type="file" accept=".csv,text/csv"
                className="hidden" onChange={onFilePick}
                data-testid="bulk-add-file-input"
              />
            </div>

            <button
              onClick={handleDownloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-orange-500/30 bg-orange-500/10 text-orange-500 hover:bg-orange-500/20 text-sm font-medium transition-colors"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" /> Download template CSV
            </button>

            <InstructionsPanel clientNameList={clientNameList} />
          </div>
        )}

        {/* ─── STEP 2: REVIEW ─── */}
        {canImport && step === "review" && parsed && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="New" value={parsed.validRows.length - parsed.matches.length} color="emerald" testid="summary-new" />
              <SummaryCard label="Updates" value={parsed.matches.length} color="amber" testid="summary-updates" />
              <SummaryCard label="Errors" value={parsed.invalidRows.length} color="red" testid="summary-errors" />
            </div>

            {parsed.invalidRows.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-1.5" data-testid="invalid-rows-panel">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-500">
                  <AlertTriangle className="w-4 h-4" /> {parsed.invalidRows.length} row(s) will be skipped — fix and re-upload to include
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 text-xs font-mono-data">
                  {parsed.invalidRows.slice(0, 20).map(r => (
                    <div key={r.idx} className="flex gap-2 items-start py-0.5">
                      <span className="text-red-400 shrink-0">Row {r.idx + 2}:</span>
                      <span className="text-red-500">{r.errors.join(" · ")}</span>
                    </div>
                  ))}
                  {parsed.invalidRows.length > 20 && (
                    <div className="text-red-400 italic text-[10px]">…and {parsed.invalidRows.length - 20} more</div>
                  )}
                </div>
              </div>
            )}

            {parsed.matches.length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="matches-panel">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-600">
                    <Copy className="w-4 h-4" /> Existing PO Lines detected ({parsed.matches.length})
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className="text-[hsl(var(--muted-foreground))] pr-1">Apply to all:</span>
                    <BulkActionPill label="Update" onClick={() => applyToAll("update")} color="orange" />
                    <BulkActionPill label="Skip" onClick={() => applyToAll("skip")} color="slate" />
                    <BulkActionPill label="Duplicate" onClick={() => applyToAll("duplicate")} color="emerald" />
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-[hsl(var(--border))]/50">
                  {parsed.matches.map(m => (
                    <MatchRow
                      key={m.rowIdx}
                      match={m}
                      decision={rowDecision(m.rowIdx)}
                      onDecision={(d) => setDecisions(s => ({ ...s, [m.rowIdx]: d }))}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => { setStep("upload"); setParsed(null); setDecisions({}); }}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] text-sm font-medium hover:bg-[hsl(var(--muted))]/80 border border-[hsl(var(--border))] transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={importing || parsed.validRows.length === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-500 disabled:bg-orange-500/30 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
                data-testid="confirm-bulk-add-btn"
              >
                {importing ? (<><Loader2 className="w-4 h-4 animate-spin" /> {progress || "Importing…"}</>) : (<><CheckCircle2 className="w-4 h-4" /> Confirm import</>)}
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: DONE ─── */}
        {canImport && step === "done" && result && (
          <div className="py-6 text-center space-y-3" data-testid="bulk-add-done">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <div>
              <p className="text-base font-semibold text-[hsl(var(--foreground))]">Import complete</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                <span className="font-mono-data text-emerald-500">{result.created}</span> created
                <span className="mx-2 text-[hsl(var(--border))]">·</span>
                <span className="font-mono-data text-amber-500">{result.updated}</span> updated
                {result.skipped > 0 && (
                  <><span className="mx-2 text-[hsl(var(--border))]">·</span><span className="font-mono-data text-red-500">{result.skipped}</span> skipped (errors)</>
                )}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => { setStep("upload"); setParsed(null); setDecisions({}); setResult(null); }}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] text-sm font-medium hover:bg-[hsl(var(--muted))]/80 border border-[hsl(var(--border))] transition-colors"
                data-testid="upload-another-btn"
              >
                <RefreshCw className="w-4 h-4" /> Upload another
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium transition-colors"
                data-testid="close-bulk-add-btn"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
function SummaryCard({ label, value, color, testid }) {
  const map = {
    emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-600" },
    amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/30",   text: "text-amber-600" },
    red:     { bg: "bg-red-500/10",     border: "border-red-500/30",     text: "text-red-500" },
  }[color];
  return (
    <div className={`rounded-lg border ${map.border} ${map.bg} p-3`} data-testid={testid}>
      <div className={`font-mono-data text-2xl font-bold ${map.text}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1 font-bold">{label}</div>
    </div>
  );
}

function BulkActionPill({ label, onClick, color }) {
  const map = {
    orange:  "bg-orange-500/15 text-orange-500 hover:bg-orange-500/25 border-orange-500/30",
    slate:   "bg-slate-500/15 text-slate-500 hover:bg-slate-500/25 border-slate-500/30",
    emerald: "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 border-emerald-500/30",
  }[color];
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors ${map}`}
      data-testid={`apply-all-${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}

/* ── Compact date formatter: 15 Jun'26 style ─────────────────────────────── */
function fmtDate(raw) {
  if (!raw) return "—";
  const s = String(raw).trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mo = months[parseInt(m[2], 10) - 1] || "";
  return `${parseInt(m[3], 10)} ${mo}'${m[1].slice(2)}`;
}

/* ── Diff pill: old → new, highlighted when changed ──────────────────────── */
function DiffPill({ label, before, after, changed }) {
  if (!changed) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 font-mono-data">
      <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold mr-0.5">{label}</span>
      <span className="text-[hsl(var(--muted-foreground))] line-through decoration-red-500/60">{before}</span>
      <span className="text-[hsl(var(--muted-foreground))]">→</span>
      <span className="text-amber-600 font-bold">{after}</span>
    </span>
  );
}

function MatchRow({ match, decision, onDecision }) {
  const { item, existing, rowIdx } = match;
  const first = existing[0];

  // Compute diffs between existing (first match) and incoming
  const diffs = [];
  const push = (label, before, after, changed) => {
    if (changed) diffs.push({ label, before, after });
  };
  const oldQty = Number(first.quantity) || 0;
  const newQty = Number(item.quantity) || 0;
  push("Qty", oldQty, newQty, oldQty !== newQty);

  const oldPrice = oldQty > 0 ? (Number(first.value_usd) || 0) / oldQty : 0;
  const newPrice = newQty > 0 ? (Number(item.value_usd) || 0) / newQty : 0;
  const oldPriceR = Math.round(oldPrice * 100) / 100;
  const newPriceR = Math.round(newPrice * 100) / 100;
  push("Unit $", `$${oldPriceR.toFixed(2)}`, `$${newPriceR.toFixed(2)}`, Math.abs(oldPriceR - newPriceR) > 0.009);

  const oldValR = Math.round((Number(first.value_usd) || 0) * 100) / 100;
  const newValR = Math.round((Number(item.value_usd) || 0) * 100) / 100;
  push("Total $", `$${oldValR.toLocaleString()}`, `$${newValR.toLocaleString()}`, Math.abs(oldValR - newValR) > 0.009);

  const oldDue = (first.due_date || "").slice(0, 10);
  const newDue = (item.due_date || "").slice(0, 10);
  push("Due", fmtDate(oldDue), fmtDate(newDue), oldDue !== newDue && !!newDue);

  const oldDD = (first.dispatch_due_date || "").slice(0, 10);
  const newDD = (item.dispatch_due_date || "").slice(0, 10);
  push("Dispatch", fmtDate(oldDD), fmtDate(newDD), oldDD !== newDD && !!newDD);

  const oldPrio = first.priority || "Standard";
  const newPrio = item.priority || "Standard";
  push("Priority", oldPrio, newPrio, oldPrio !== newPrio);

  const oldVendor = (first.vendor || "").trim();
  const newVendor = (item.vendor || "").trim();
  push("Vendor", oldVendor || "—", newVendor || "—", oldVendor !== newVendor && !!newVendor);

  const oldQual = !!first.is_qual;
  const newQual = !!item.is_qual;
  push("QUAL", oldQual ? "Yes" : "No", newQual ? "Yes" : "No", oldQual !== newQual);

  const oldPart = (first.part_number || "").trim();
  const newPart = (item.part_number || "").trim();
  push("Part #", oldPart || "—", newPart || "—", oldPart !== newPart && !!newPart);

  const oldDesc = (first.item_description || "").trim();
  const newDesc = (item.item_description || "").trim();
  const descChanged = oldDesc !== newDesc;

  const choiceClass = (val) => decision === val
    ? "bg-orange-500 text-white border-orange-500"
    : "bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))] hover:border-orange-500/50";

  return (
    <div className="py-2.5 px-1" data-testid={`match-row-${rowIdx}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          {/* Row 1: Identity */}
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="font-mono-data text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30">
              <span className="text-[9px] font-bold uppercase tracking-widest opacity-70 mr-1">PO</span>{item.po_number}
              <span className="opacity-40 mx-1">·</span>
              <span className="text-[9px] font-bold uppercase tracking-widest opacity-70 mr-1">LINE</span>{item.line_number}
            </span>
            {newPart && (
              <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
                Part: {newPart}
              </span>
            )}
            {first.stage && first.stage !== "PO Received" && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0 rounded border"
                style={{
                  color: STAGE_BADGE_COLORS[first.stage] || "#94a3b8",
                  borderColor: (STAGE_BADGE_COLORS[first.stage] || "#94a3b8") + "66",
                  backgroundColor: (STAGE_BADGE_COLORS[first.stage] || "#94a3b8") + "18",
                }}
                title="This item is already past PO Received — only customer-revision fields will flow through"
                data-testid={`match-row-stage-${rowIdx}`}
              >
                In&nbsp;{first.stage}
              </span>
            )}
            {existing.length > 1 && <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{existing.length} existing matches</span>}
            {diffs.length === 0 && !descChanged && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-500 border border-slate-500/30 font-bold uppercase tracking-wider">No changes</span>
            )}
          </div>

          {/* Row 2: Description diff (if changed) */}
          {descChanged && (
            <div className="text-xs space-y-0.5">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]/70 font-bold shrink-0 w-16">Existing</span>
                <span className="truncate text-[hsl(var(--muted-foreground))] line-through decoration-red-500/40">{oldDesc || "—"}</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[9px] uppercase tracking-wider text-amber-600 font-bold shrink-0 w-16">Incoming</span>
                <span className="truncate text-amber-600 font-medium">{newDesc || "—"}</span>
              </div>
            </div>
          )}

          {/* Row 3: Diff pills for changed fields */}
          {diffs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`diffs-${rowIdx}`}>
              {diffs.map((d) => (
                <DiffPill key={d.label} label={d.label} before={d.before} after={d.after} changed={true} />
              ))}
            </div>
          )}

          {/* If nothing changed show the matching description for context */}
          {!descChanged && diffs.length === 0 && (
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
              {oldDesc || "—"}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0" role="group">
          {["update", "skip", "duplicate"].map(v => (
            <button
              key={v}
              onClick={() => onDecision(v)}
              className={`px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors ${choiceClass(v)}`}
              data-testid={`row-${rowIdx}-${v}`}
            >
              {v === "duplicate" ? "Dup" : v}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function InstructionsPanel({ clientNameList }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-4 text-xs space-y-3" data-testid="instructions-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--foreground))]">
        <Info className="w-4 h-4 text-orange-500" /> Instructions
      </div>
      <ul className="space-y-1.5 text-[hsl(var(--muted-foreground))] leading-relaxed">
        <li>• <strong className="text-[hsl(var(--foreground))]">Required</strong>: PO No., Item No., Item Description, Customer, Due date.</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Dates</strong>: accepts DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD.</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Quantity</strong>: Confirmed Quantity is used (falls back to Requested Quantity).</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Value</strong>: calculated as Quantity × Requested Unit Price (USD).</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Priority</strong>: "Expedite" / "Urgent" / "Rush" map to Expedite; everything else is Standard (default).</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">QUAL</strong>: "Yes", "Y", "True", "X" → QUAL flag on; anything else off.</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Unit defaults</strong> to <code className="font-mono-data">pcs</code>.</li>
        <li>• <strong className="text-[hsl(var(--foreground))]">Stage defaults</strong> to <code className="font-mono-data">PO Received</code>.</li>
      </ul>
      <div className="pt-2 border-t border-[hsl(var(--border))]/50">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5">Accepted Customer values ({clientNameList.length})</div>
        <div className="flex flex-wrap gap-1">
          {clientNameList.length === 0 && <span className="italic text-[hsl(var(--muted-foreground))]">No clients in DB — add via Admin Settings first.</span>}
          {clientNameList.map(c => (
            <span key={c} className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded bg-orange-500/10 text-orange-500 border border-orange-500/20">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
